import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';

const HeaderContainer = styled.header`
  position: relative;
  z-index: 1000;
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0;
  background: var(--bg);
  border: none;
  box-shadow: none;
`;

const HeaderContent = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 2.2rem;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 640px) {
    padding: 0 1rem;
  }
`;

const Nav = styled.nav`
  display: inline-flex;
  align-items: center;
  gap: 24px;
  flex-wrap: nowrap;

  @media (max-width: 640px) {
    gap: 0.95rem;
  }
`;

const NavLink = styled.a`
  display: inline-block;
  font-family: var(--font-sans);
  font-size: 16px;
  padding: 8px 0;
  font-weight: ${(p) => (p.$active ? 500 : 400)};
  letter-spacing: -0.01em;
  color: ${(p) => (p.$active ? 'var(--text)' : 'var(--nav-muted)')};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text);
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const ThemeToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background: transparent;
  color: var(--nav-muted);
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text);
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 3px;
    border-radius: 2px;
  }

  svg {
    width: 1.05rem;
    height: 1.05rem;
  }
`;

const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const navItems = [
  { id: 'work', label: 'Work', href: '/' },
  { id: 'about', label: 'About', href: '/about' },
  { id: 'blog', label: 'Blog', href: '/blog' },
];

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const activeNav = useMemo(() => {
    if (location.pathname === '/about') return 'about';
    if (location.pathname === '/blog') return 'blog';
    if (location.pathname === '/' || location.pathname === '/work') return 'work';
    return '';
  }, [location.pathname]);

  const handleNavClick = (e, item) => {
    e.preventDefault();
    if (item === 'work') {
      navigate('/');
      return;
    }
    navigate(`/${item}`);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Nav>
          {navItems.map(({ id, label, href }) => (
            <NavLink
              key={id}
              $active={activeNav === id}
              href={href}
              onClick={(e) => handleNavClick(e, id)}
            >
              {label}
            </NavLink>
          ))}
          <ThemeToggle
            type="button"
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}
          >
            {theme === 'light' ? <MoonIcon /> : <SunIcon />}
          </ThemeToggle>
        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
